import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";

const CONTENT = {
  fr: {
    title: "Politique de confidentialité",
    updated: "Dernière mise à jour : juin 2026",
    back: "Retour",
    sections: [
      {
        h: "1. Qui sommes-nous",
        p: ["SHIALOC (accessible sur https://shialoc.com) est une plateforme permettant aux associations, centres islamiques et mosquées de publier leurs programmes et événements, et à leurs membres de les suivre. Responsable du traitement et contact : amiriaz2000@gmail.com."],
      },
      {
        h: "2. Données que nous collectons",
        p: [
          "Données de compte : nom, adresse email et mot de passe (stocké uniquement sous forme hachée, jamais en clair).",
          "Données d'utilisation : associations et mosquées suivies, inscriptions aux événements, publications, commentaires et réactions dans le fil communautaire.",
          "Données techniques : cookie de session sécurisé (httpOnly) nécessaire à la connexion, et identifiant de notification push si vous activez les notifications.",
        ],
      },
      {
        h: "3. Données Google (synchronisation d'agenda)",
        p: [
          "Si vous choisissez de connecter votre agenda Google, SHIALOC demande l'autorisation d'accéder à votre agenda Google (API Google Calendar) ainsi qu'à votre adresse email Google (affichée uniquement pour identifier le compte connecté).",
          "Utilisation : cette autorisation sert exclusivement à ajouter, mettre à jour et supprimer dans votre agenda les événements publiés par les associations que vous avez choisies. Nous ne lisons pas vos autres événements, nous ne parcourons pas votre agenda et nous n'utilisons aucune donnée Google à des fins publicitaires.",
          "Stockage : les jetons d'accès Google sont stockés de manière sécurisée sur nos serveurs et ne sont jamais partagés avec des tiers.",
          "Révocation : vous pouvez déconnecter votre agenda à tout moment depuis le menu « Synchroniser » de l'application (les événements synchronisés sont alors retirés de votre agenda), ou révoquer l'accès depuis https://myaccount.google.com/permissions.",
          "L'utilisation des informations reçues des API Google par SHIALOC respecte les règles relatives aux données utilisateur des services d'API Google (Google API Services User Data Policy), y compris les exigences d'utilisation limitée (Limited Use).",
        ],
      },
      {
        h: "4. Utilisation de vos données",
        p: [
          "Vos données servent uniquement à faire fonctionner le service : affichage des programmes, gestion de vos inscriptions, envoi des notifications que vous avez activées (push et/ou email) et synchronisation d'agenda si vous l'avez demandée.",
          "Nous ne vendons ni ne louons vos données personnelles. Nous n'affichons pas de publicité.",
        ],
      },
      {
        h: "5. Services tiers",
        p: [
          "OneSignal (notifications push web) — reçoit un identifiant technique de votre navigateur si vous activez les notifications.",
          "Resend (envoi d'emails) — reçoit votre adresse email pour l'envoi des messages du service.",
          "Google Calendar API — uniquement si vous connectez votre agenda (voir section 3).",
        ],
      },
      {
        h: "6. Conservation et suppression",
        p: [
          "Vos données sont conservées tant que votre compte est actif. Vous pouvez demander la suppression de votre compte et de vos données à tout moment en écrivant à amiriaz2000@gmail.com ; la suppression est effectuée dans un délai de 30 jours.",
        ],
      },
      {
        h: "7. Sécurité",
        p: [
          "Les échanges sont chiffrés (HTTPS), les mots de passe sont hachés (bcrypt) et les cookies de session sont protégés (httpOnly, secure). L'accès aux données est limité au fonctionnement du service.",
        ],
      },
      {
        h: "8. Vos droits",
        p: [
          "Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de suppression et de portabilité de vos données. Pour exercer ces droits : amiriaz2000@gmail.com.",
        ],
      },
      {
        h: "9. Modifications",
        p: [
          "Cette politique peut être mise à jour ; la date de dernière mise à jour figure en haut de cette page. En cas de changement important, les utilisateurs connectés en seront informés dans l'application.",
        ],
      },
    ],
  },
  en: {
    title: "Privacy Policy",
    updated: "Last updated: June 2026",
    back: "Back",
    sections: [
      {
        h: "1. Who we are",
        p: ["SHIALOC (available at https://shialoc.com) is a platform enabling associations, Islamic centers and mosques to publish their programs and events, and their members to follow them. Data controller and contact: amiriaz2000@gmail.com."],
      },
      {
        h: "2. Data we collect",
        p: [
          "Account data: name, email address and password (stored only as a hash, never in plain text).",
          "Usage data: followed associations and mosques, event registrations, posts, comments and reactions in the community feed.",
          "Technical data: a secure session cookie (httpOnly) required for login, and a push notification identifier if you enable notifications.",
        ],
      },
      {
        h: "3. Google data (calendar sync)",
        p: [
          "If you choose to connect your Google Calendar, SHIALOC requests permission to access your Google Calendar (Google Calendar API) and your Google email address (displayed only to identify the connected account).",
          "Use: this permission is used exclusively to add, update and delete in your calendar the events published by the associations you selected. We do not read your other events, we do not browse your calendar, and we never use Google data for advertising.",
          "Storage: Google access tokens are stored securely on our servers and are never shared with third parties.",
          "Revocation: you can disconnect your calendar at any time from the app's \"Sync\" menu (synced events are then removed from your calendar), or revoke access at https://myaccount.google.com/permissions.",
          "SHIALOC's use of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.",
        ],
      },
      {
        h: "4. How we use your data",
        p: [
          "Your data is used only to operate the service: displaying programs, managing your registrations, sending the notifications you enabled (push and/or email) and syncing your calendar if requested.",
          "We do not sell or rent your personal data. We do not display advertising.",
        ],
      },
      {
        h: "5. Third-party services",
        p: [
          "OneSignal (web push notifications) — receives a technical identifier from your browser if you enable notifications.",
          "Resend (email delivery) — receives your email address to send service messages.",
          "Google Calendar API — only if you connect your calendar (see section 3).",
        ],
      },
      {
        h: "6. Retention and deletion",
        p: [
          "Your data is kept as long as your account is active. You can request deletion of your account and data at any time by writing to amiriaz2000@gmail.com; deletion is carried out within 30 days.",
        ],
      },
      {
        h: "7. Security",
        p: [
          "All traffic is encrypted (HTTPS), passwords are hashed (bcrypt) and session cookies are protected (httpOnly, secure). Data access is limited to operating the service.",
        ],
      },
      {
        h: "8. Your rights",
        p: [
          "In accordance with the GDPR, you have the right to access, rectify, delete and port your data. To exercise these rights: amiriaz2000@gmail.com.",
        ],
      },
      {
        h: "9. Changes",
        p: [
          "This policy may be updated; the last update date is shown at the top of this page. In case of significant changes, logged-in users will be informed in the app.",
        ],
      },
    ],
  },
};

export default function Privacy() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.fr;
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => window.history.back()} data-testid="privacy-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> {c.back}
        </button>
        <div className="flex items-center gap-3 mb-2">
          <span className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </span>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl" data-testid="privacy-title">{c.title}</h1>
        </div>
        <p className="text-xs text-muted-foreground mb-10">{c.updated}</p>
        <div className="space-y-8">
          {c.sections.map((s, i) => (
            <section key={i} data-testid={`privacy-section-${i}`}>
              <h2 className="font-heading font-bold text-lg mb-3">{s.h}</h2>
              <div className="space-y-2.5">
                {s.p.map((txt, j) => (
                  <p key={j} className="text-sm text-muted-foreground leading-relaxed">{txt}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
        <div className="mt-12 pt-6 border-t border-border">
          <Link to="/" className="text-sm text-primary hover:underline" data-testid="privacy-home-link">SHIALOC — shialoc.com</Link>
        </div>
      </div>
    </div>
  );
}
